import React, { useState, useEffect, useCallback } from 'react';
import { workOrdersAPI, userAPI } from '../../../services/api';
import WorkOrderModal from '../../WorkOrderModal/WorkOrderModal';

// Work orders belonging to one project. List + create/edit/delete. The modal's job
// selector is locked to this project.
const WorkOrdersTab = ({ project, onCountChange }) => {
  const [workOrders, setWorkOrders] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    if (!project?.id) return;
    try {
      setLoading(true);
      const res = await workOrdersAPI.getAll({ projectId: project.id });
      setWorkOrders(res.data || []);
      onCountChange && onCountChange((res.data || []).length);
    } catch (err) {
      console.error('Failed to load work orders:', err);
    } finally {
      setLoading(false);
    }
  }, [project?.id, onCountChange]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { userAPI.getAll().then((r) => setUsers(r.data || [])).catch(() => {}); }, []);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this work order?')) return;
    try {
      await workOrdersAPI.delete(id);
      load();
    } catch {
      alert('Could not delete that work order.');
    }
  };

  const openCreate = () => { setEditing(null); setModalOpen(true); };
  const openEdit = (wo) => { setEditing(wo); setModalOpen(true); };

  const money = (v) => (v != null ? `$${Number(v).toLocaleString()}` : '—');
  const date = (d) => (d ? new Date(d).toLocaleDateString() : '—');

  return (
    <div className="workorders-tab">
      <div className="tab-header">
        <h3>Additional Work ({workOrders.length})</h3>
        <button className="btn-create-task" onClick={openCreate}>+ Add Additional Work</button>
      </div>

      {loading ? (
        <div className="tab-empty"><p>Loading…</p></div>
      ) : workOrders.length === 0 ? (
        <div className="tab-empty">
          <i className="fas fa-clipboard-list"></i>
          <p>No work orders for this job yet</p>
        </div>
      ) : (
        <div className="tasks-table-container">
          <table className="tasks-table">
            <thead>
              <tr>
                <th>Work</th>
                <th>Type</th>
                <th>Assigned To</th>
                <th>Subcontractor</th>
                <th>Amount</th>
                <th>Scheduled</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {workOrders.map((wo) => (
                <tr key={wo.id} className="task-row">
                  <td className="task-info">
                    <div className="task-title">{wo.title}</div>
                    {wo.description && <div className="task-description">{wo.description}</div>}
                  </td>
                  <td>{wo.type || '—'}</td>
                  <td className="assigned-to">{wo.assignedToName || 'Unassigned'}</td>
                  <td className="assigned-to">{wo.subcontractorName || '—'}</td>
                  <td>{money(wo.amount)}</td>
                  <td className="due-date">{date(wo.scheduledDate)}</td>
                  <td><span className="status-badge">{wo.status}</span></td>
                  <td className="task-actions">
                    <div className="action-buttons">
                      <button className="action-btn btn-edit" onClick={() => openEdit(wo)} title="Edit">
                        <i className="fas fa-edit"></i>
                      </button>
                      <button className="action-btn btn-delete" onClick={() => handleDelete(wo.id)} title="Delete">
                        <i className="fas fa-trash-alt"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modalOpen && (
        <WorkOrderModal
          isOpen={modalOpen}
          onClose={() => setModalOpen(false)}
          onSaved={() => { setModalOpen(false); load(); }}
          projects={[{ id: project.id, projectName: project.projectName }]}
          users={users}
          workOrder={editing}
          lockedProjectId={project.id}
        />
      )}
    </div>
  );
};

export default WorkOrdersTab;
